/**
 * Accessibility Enhancements for The Cow Lot
 * WCAG 2.1 AA Compliance - Third-Party App Fixes
 *
 * This script dynamically enhances accessibility for elements
 * that are injected by third-party apps (Optis, review apps, etc.)
 */

(function() {
  'use strict';

  const A11yEnhancements = {
    /**
     * Initialize all accessibility enhancements
     */
    init: function() {
      // Run immediately
      this.enhanceAll();

      // Run after DOM is fully loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.enhanceAll());
      }

      // Observe for dynamically added content (third-party apps)
      this.observeDOMChanges();

      // Re-run after a delay for slow-loading third-party scripts
      setTimeout(() => this.enhanceAll(), 2000);
      setTimeout(() => this.enhanceAll(), 5000);
    },

    /**
     * Run all enhancement functions
     */
    enhanceAll: function() {
      this.enhanceFormInputs();
      this.enhanceSelectElements();
      this.enhanceButtons();
      this.enhanceImages();
      this.enhanceLinks();
      this.enhanceRequiredFields();
      this.enhanceErrorMessages();
      this.announceVisibilityChanges();
      this.enhanceOptisProductOptions();
      this.fixAriaHiddenConflicts();
      this.enhanceColorSwatches();
      this.enhanceOptisImages();
    },

    /**
     * Observe DOM for changes and re-apply enhancements
     */
    observeDOMChanges: function() {
      // Removed: body-wide MutationObserver was firing on every hat builder
      // DOM change, causing major slowdowns. The setTimeout delays in init()
      // (2s and 5s) already cover late-loading third-party content.
    },

    /**
     * Enhance form inputs without proper labels
     */
    enhanceFormInputs: function() {
      const inputs = document.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"])');

      inputs.forEach((input) => {
        // Skip if already has accessible label
        if (this.hasAccessibleLabel(input)) return;

        const id = input.id || this.generateId('input');
        input.id = id;

        // Try to find label text from various sources
        let labelText = this.findLabelText(input);

        if (labelText) {
          // Create visually hidden label if one doesn't exist
          if (!document.querySelector(`label[for="${id}"]`)) {
            // First try aria-label
            input.setAttribute('aria-label', labelText);
          }
        }
      });
    },

    /**
     * Enhance select elements without proper labels
     */
    enhanceSelectElements: function() {
      const selects = document.querySelectorAll('select');

      selects.forEach((select) => {
        // Skip if already has accessible label
        if (this.hasAccessibleLabel(select)) return;

        const id = select.id || this.generateId('select');
        select.id = id;

        let labelText = this.findLabelText(select);

        // If no label found, try to use first option or name attribute
        if (!labelText) {
          const firstOption = select.querySelector('option');
          if (firstOption && firstOption.textContent.includes('Select') || firstOption && firstOption.textContent.includes('Choose')) {
            labelText = firstOption.textContent.trim();
          } else if (select.name) {
            labelText = this.humanizeName(select.name);
          }
        }

        if (labelText) {
          select.setAttribute('aria-label', labelText);
        }
      });
    },

    /**
     * Enhance buttons without accessible names
     */
    enhanceButtons: function() {
      const buttons = document.querySelectorAll('button, [role="button"]');

      buttons.forEach((button) => {
        // Skip if already has accessible name
        if (button.textContent.trim() || button.getAttribute('aria-label') || button.getAttribute('title')) return;

        // Check for SVG or image inside
        const svg = button.querySelector('svg');
        const img = button.querySelector('img');

        let label = '';

        if (svg) {
          // Try to determine button purpose from class or parent
          const classes = button.className;
          if (classes.includes('close')) label = 'Close';
          else if (classes.includes('prev') || classes.includes('previous')) label = 'Previous';
          else if (classes.includes('next')) label = 'Next';
          else if (classes.includes('play')) label = 'Play';
          else if (classes.includes('pause')) label = 'Pause';
          else if (classes.includes('mute') || classes.includes('sound')) label = 'Toggle sound';
          else if (classes.includes('search')) label = 'Search';
          else if (classes.includes('menu')) label = 'Menu';
          else if (classes.includes('cart')) label = 'Cart';
          else if (classes.includes('plus') || classes.includes('add')) label = 'Increase quantity';
          else if (classes.includes('minus') || classes.includes('remove')) label = 'Decrease quantity';
          else if (classes.includes('delete') || classes.includes('trash')) label = 'Remove item';
          else if (classes.includes('zoom')) label = 'Zoom image';
          else if (classes.includes('expand')) label = 'Expand';
          else if (classes.includes('collapse')) label = 'Collapse';
        }

        if (img && img.alt) {
          label = img.alt;
        }

        if (label) {
          button.setAttribute('aria-label', label);
        }
      });
    },

    /**
     * Enhance images without alt text
     */
    enhanceImages: function() {
      const images = document.querySelectorAll('img:not([alt])');

      images.forEach((img) => {
        // Check if image is decorative (inside aria-hidden or button with label)
        const parent = img.closest('[aria-hidden="true"], button[aria-label], a[aria-label]');
        if (parent) {
          img.setAttribute('alt', '');
          return;
        }

        // Try to generate alt from various sources
        let alt = '';

        // Check for nearby text
        const figure = img.closest('figure');
        if (figure) {
          const figcaption = figure.querySelector('figcaption');
          if (figcaption) alt = figcaption.textContent.trim();
        }

        // Check for title attribute
        if (!alt && img.title) {
          alt = img.title;
        }

        // Use filename as last resort (better than nothing)
        if (!alt) {
          const src = img.src || img.dataset.src;
          if (src) {
            const filename = src.split('/').pop().split('?')[0].split('.')[0];
            alt = this.humanizeName(filename);
          }
        }

        if (alt) {
          img.setAttribute('alt', alt);
        } else {
          // Mark as decorative if we can't determine purpose
          img.setAttribute('alt', '');
          img.setAttribute('role', 'presentation');
        }
      });
    },

    /**
     * Enhance links without accessible text
     */
    enhanceLinks: function() {
      const links = document.querySelectorAll('a');

      links.forEach((link) => {
        // Skip if has text content or aria-label
        if (link.textContent.trim() || link.getAttribute('aria-label')) return;

        // Check for image with alt
        const img = link.querySelector('img[alt]');
        if (img && img.alt) {
          link.setAttribute('aria-label', img.alt);
          return;
        }

        // Check for title
        if (link.title) {
          link.setAttribute('aria-label', link.title);
          return;
        }

        // Try to determine from href or class
        const href = link.href;
        const classes = link.className;

        if (href && href.includes('facebook')) link.setAttribute('aria-label', 'Facebook');
        else if (href && href.includes('instagram')) link.setAttribute('aria-label', 'Instagram');
        else if (href && href.includes('twitter') || href && href.includes('x.com')) link.setAttribute('aria-label', 'Twitter');
        else if (href && href.includes('youtube')) link.setAttribute('aria-label', 'YouTube');
        else if (href && href.includes('pinterest')) link.setAttribute('aria-label', 'Pinterest');
        else if (href && href.includes('tiktok')) link.setAttribute('aria-label', 'TikTok');
        else if (classes.includes('review')) link.setAttribute('aria-label', 'View reviews');
      });
    },

    /**
     * Enhance required field indicators
     */
    enhanceRequiredFields: function() {
      // Find inputs marked as required but without aria-required
      const required = document.querySelectorAll('[required]:not([aria-required])');
      required.forEach((el) => {
        el.setAttribute('aria-required', 'true');
      });

      // Find fields with * indicator in label but no required attribute
      const labels = document.querySelectorAll('label');
      labels.forEach((label) => {
        if (label.textContent.includes('*')) {
          const forId = label.getAttribute('for');
          if (forId) {
            const input = document.getElementById(forId);
            if (input && !input.hasAttribute('aria-required')) {
              input.setAttribute('aria-required', 'true');
            }
          }
        }
      });
    },

    /**
     * Link error messages to form fields
     */
    enhanceErrorMessages: function() {
      // Find error messages and link them to inputs
      const errorMessages = document.querySelectorAll('.error, .error-message, [class*="error"], .form-error, .field-error');

      errorMessages.forEach((error) => {
        if (!error.id) {
          error.id = this.generateId('error');
        }

        // Find related input
        const field = error.closest('.field, .form-group, .form-field');
        if (field) {
          const input = field.querySelector('input, select, textarea');
          if (input) {
            const existingDescribedBy = input.getAttribute('aria-describedby') || '';
            if (!existingDescribedBy.includes(error.id)) {
              input.setAttribute('aria-describedby', (existingDescribedBy + ' ' + error.id).trim());
              input.setAttribute('aria-invalid', 'true');
            }
          }
        }
      });
    },

    /**
     * Set up announcements for visibility changes (WCAG 4.1.3)
     */
    announceVisibilityChanges: function() {
      // Create live region if it doesn't exist
      if (!document.getElementById('a11y-announcer')) {
        const announcer = document.createElement('div');
        announcer.id = 'a11y-announcer';
        announcer.setAttribute('aria-live', 'polite');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.className = 'visually-hidden';
        announcer.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;';
        document.body.appendChild(announcer);
      }
    },

    /**
     * Announce a message to screen readers
     */
    announce: function(message) {
      const announcer = document.getElementById('a11y-announcer');
      if (announcer) {
        announcer.textContent = '';
        setTimeout(() => {
          announcer.textContent = message;
        }, 100);
      }
    },

    /**
     * Enhance Optis/BSS Product Options for accessibility
     * Targets common BSS class patterns and improves keyboard/screen reader support
     */
    enhanceOptisProductOptions: function() {
      // Enhance dropdown menus with thumbnails
      this.enhanceOptisDropdowns();

      // Enhance swatch/thumbnail selectors
      this.enhanceOptisSwatches();

      // Enhance radio button groups
      this.enhanceOptisRadios();

      // Enhance file upload fields
      this.enhanceOptisFileUpload();

      // Add fieldset/legend structure for option groups
      this.enhanceOptisGrouping();

      // Announce option changes to screen readers
      this.setupOptisChangeAnnouncements();
    },

    /**
     * Enhance Optis dropdown selects
     */
    enhanceOptisDropdowns: function() {
      const selectors = [
        '[class*="bss-po"] select',
        '[class*="bss_po"] select',
        '.bss-po-select',
        '.bss_po_select'
      ];

      const dropdowns = document.querySelectorAll(selectors.join(','));

      dropdowns.forEach((select) => {
        // Ensure accessible label
        if (!this.hasAccessibleLabel(select)) {
          // Try to find label from parent wrapper
          const wrapper = select.closest('[class*="bss-po-option"], [class*="bss_po_option"]');
          if (wrapper) {
            const labelEl = wrapper.querySelector('[class*="label"], span:first-child, .bss-po-label');
            if (labelEl) {
              const labelText = labelEl.textContent.replace('*', '').trim();
              select.setAttribute('aria-label', labelText);
            }
          }
        }

        // Add aria-required if marked as required
        const wrapper = select.closest('[class*="bss-po-option"], [class*="bss_po_option"]');
        if (wrapper) {
          const required = wrapper.querySelector('.required, [class*="required"]');
          if (required || wrapper.textContent.includes('*')) {
            select.setAttribute('aria-required', 'true');
          }
        }

        // Improve default option text for screen readers
        const firstOption = select.querySelector('option:first-child');
        if (firstOption && (firstOption.value === '' || firstOption.textContent.includes('--'))) {
          if (!firstOption.textContent.includes('Select') && !firstOption.textContent.includes('Choose')) {
            const label = select.getAttribute('aria-label') || 'an option';
            firstOption.textContent = `Select ${label}`;
          }
        }
      });
    },

    /**
     * Enhance Optis swatch/thumbnail selectors
     */
    enhanceOptisSwatches: function() {
      const selectors = [
        '[class*="bss-po-swatch"]',
        '[class*="bss_po_swatch"]',
        '[class*="bss-po-thumb"]',
        '[class*="bss_po_thumb"]'
      ];

      const swatches = document.querySelectorAll(selectors.join(','));

      swatches.forEach((swatch) => {
        // Ensure swatches are keyboard accessible
        if (!swatch.hasAttribute('tabindex')) {
          swatch.setAttribute('tabindex', '0');
        }

        // Add button role if not already a button
        if (swatch.tagName !== 'BUTTON' && swatch.tagName !== 'INPUT' && !swatch.hasAttribute('role')) {
          swatch.setAttribute('role', 'button');
        }

        // Find and set accessible name
        if (!swatch.getAttribute('aria-label')) {
          // Try title text
          const title = swatch.querySelector('[class*="title"], [class*="label"], span');
          if (title) {
            swatch.setAttribute('aria-label', title.textContent.trim());
          }
          // Try image alt
          const img = swatch.querySelector('img');
          if (img && img.alt) {
            swatch.setAttribute('aria-label', img.alt);
          }
        }

        // Add selected state for screen readers
        if (swatch.classList.contains('selected') || swatch.classList.contains('active')) {
          swatch.setAttribute('aria-pressed', 'true');
        } else {
          swatch.setAttribute('aria-pressed', 'false');
        }

        // Add keyboard support
        if (!swatch.dataset.a11yKeyboard) {
          swatch.dataset.a11yKeyboard = 'true';
          swatch.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              swatch.click();
            }
          });
        }
      });
    },

    /**
     * Enhance Optis radio button groups
     */
    enhanceOptisRadios: function() {
      const selectors = [
        '[class*="bss-po-radio"]',
        '[class*="bss_po_radio"]',
        '[class*="bss-po-checkbox"]',
        '[class*="bss_po_checkbox"]'
      ];

      const groups = document.querySelectorAll(selectors.join(','));

      groups.forEach((group) => {
        // Ensure proper grouping with role
        if (!group.getAttribute('role')) {
          const hasCheckboxes = group.querySelector('input[type="checkbox"]');
          group.setAttribute('role', hasCheckboxes ? 'group' : 'radiogroup');
        }

        // Find group label
        const wrapper = group.closest('[class*="bss-po-option"], [class*="bss_po_option"]');
        if (wrapper && !group.getAttribute('aria-label')) {
          const labelEl = wrapper.querySelector('[class*="label"], .bss-po-label');
          if (labelEl) {
            const labelText = labelEl.textContent.replace('*', '').trim();
            group.setAttribute('aria-label', labelText);
          }
        }
      });
    },

    /**
     * Enhance Optis file upload fields
     */
    enhanceOptisFileUpload: function() {
      const selectors = [
        '[class*="bss-po-file"]',
        '[class*="bss_po_file"]',
        '[class*="bss-po-upload"]',
        '[class*="bss_po_upload"]'
      ];

      const uploads = document.querySelectorAll(selectors.join(','));

      uploads.forEach((upload) => {
        const input = upload.querySelector('input[type="file"]');
        if (input) {
          // Ensure accessible label
          if (!this.hasAccessibleLabel(input)) {
            const wrapper = input.closest('[class*="bss-po-option"]');
            if (wrapper) {
              const labelEl = wrapper.querySelector('[class*="label"], .bss-po-label');
              if (labelEl) {
                input.setAttribute('aria-label', labelEl.textContent.replace('*', '').trim());
              }
            }

            if (!input.getAttribute('aria-label')) {
              input.setAttribute('aria-label', 'Upload file');
            }
          }

          // Add helpful description
          if (!input.getAttribute('aria-describedby')) {
            const helpText = upload.querySelector('[class*="help"], [class*="desc"], .bss-po-helptext');
            if (helpText) {
              const helpId = helpText.id || this.generateId('help');
              helpText.id = helpId;
              input.setAttribute('aria-describedby', helpId);
            }
          }
        }
      });
    },

    /**
     * Add fieldset/legend structure for option groups
     */
    enhanceOptisGrouping: function() {
      const optionGroups = document.querySelectorAll('[class*="bss-po-option-wrap"], [class*="bss_po_option_wrap"]');

      optionGroups.forEach((group) => {
        // Skip if already has proper grouping
        if (group.getAttribute('role') === 'group') return;

        const labelEl = group.querySelector('[class*="label"]:first-child, .bss-po-label');
        const hasMultipleInputs = group.querySelectorAll('input, select').length > 1;

        if (labelEl && hasMultipleInputs) {
          group.setAttribute('role', 'group');

          const labelId = labelEl.id || this.generateId('group-label');
          labelEl.id = labelId;
          group.setAttribute('aria-labelledby', labelId);
        }
      });
    },

    /**
     * Fix aria-hidden conflicts on visible elements
     * Elements with aria-hidden="true" should not be visible/focusable
     */
    fixAriaHiddenConflicts: function() {
      const hiddenElements = document.querySelectorAll('[aria-hidden="true"]');

      hiddenElements.forEach((el) => {
        // Check if element is actually visible
        const style = window.getComputedStyle(el);
        const isVisible = style.display !== 'none' &&
                          style.visibility !== 'hidden' &&
                          style.opacity !== '0' &&
                          el.offsetParent !== null;

        if (isVisible) {
          // Check if it contains focusable elements
          const focusables = el.querySelectorAll('a, button, input, select, textarea, [tabindex]');
          const hasFocusableContent = focusables.length > 0;

          // Check if it's decorative (SVG icons, decorative images)
          const isDecorative = el.tagName === 'SVG' ||
                               (el.tagName === 'IMG' && (el.alt === '' || el.getAttribute('role') === 'presentation')) ||
                               el.classList.contains('icon') ||
                               el.classList.contains('decorative');

          if (!isDecorative && hasFocusableContent) {
            // Remove aria-hidden from visible interactive elements
            el.removeAttribute('aria-hidden');
          } else if (!isDecorative && el.textContent.trim()) {
            // Has meaningful text content - should be accessible
            el.removeAttribute('aria-hidden');
          }
        }

        // Ensure truly hidden elements have tabindex=-1 on focusables
        if (el.getAttribute('aria-hidden') === 'true') {
          const focusables = el.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          focusables.forEach((focusable) => {
            focusable.setAttribute('tabindex', '-1');
          });
        }
      });

      // Also check for elements that should have aria-hidden but don't
      const decorativeElements = document.querySelectorAll('.icon:not([aria-hidden]), svg.icon:not([aria-hidden])');
      decorativeElements.forEach((el) => {
        if (!el.textContent.trim() && !el.getAttribute('aria-label')) {
          el.setAttribute('aria-hidden', 'true');
        }
      });
    },

    /**
     * Enhance color swatches with proper accessibility attributes
     * Addresses common swatch patterns from various apps
     */
    enhanceColorSwatches: function() {
      const swatchSelectors = [
        '.color-swatch',
        '.swatch-element',
        '[class*="color-swatch"]',
        '[class*="swatch-color"]',
        '[data-swatch]',
        '.variant-swatch',
        '[class*="bss-po-color"]',
        '[class*="bss_po_color"]',
        '.color-option',
        'label[data-color]',
        'label.color',
        'input[type="radio"][name*="color"] + label',
        'input[type="radio"][name*="Color"] + label'
      ];

      const swatches = document.querySelectorAll(swatchSelectors.join(','));

      swatches.forEach((swatch) => {
        // Skip if already has accessible label
        if (swatch.getAttribute('aria-label') || swatch.getAttribute('title')) return;

        let colorName = '';

        // Try various sources for the color name
        // 1. Check data attributes
        colorName = swatch.dataset.swatch ||
                    swatch.dataset.color ||
                    swatch.dataset.value ||
                    swatch.dataset.optionValue ||
                    '';

        // 2. Check for associated input value
        if (!colorName) {
          const input = swatch.querySelector('input') || swatch.previousElementSibling;
          if (input && input.tagName === 'INPUT') {
            colorName = input.value || input.dataset.value || '';
          }
        }

        // 3. Check for title attribute on child elements
        if (!colorName) {
          const titled = swatch.querySelector('[title]');
          if (titled) colorName = titled.title;
        }

        // 4. Check parent element for color info
        if (!colorName) {
          const parent = swatch.closest('[data-color], [data-value]');
          if (parent) {
            colorName = parent.dataset.color || parent.dataset.value || '';
          }
        }

        // 5. Try to extract from class name (e.g., "swatch-black", "color-red")
        if (!colorName) {
          const classMatch = swatch.className.match(/(?:swatch|color)[-_]([a-zA-Z]+)/);
          if (classMatch) {
            colorName = classMatch[1];
          }
        }

        // 6. Check for visible text
        if (!colorName) {
          const text = swatch.textContent.trim();
          if (text && text.length < 30) {
            colorName = text;
          }
        }

        if (colorName) {
          // Clean up the color name
          colorName = this.humanizeName(colorName);
          swatch.setAttribute('aria-label', colorName + ' color');

          // If it's acting as a button, add appropriate role
          if (!swatch.hasAttribute('role') && swatch.tagName !== 'BUTTON' && swatch.tagName !== 'INPUT') {
            if (swatch.closest('[role="radiogroup"]') || swatch.querySelector('input[type="radio"]')) {
              // Don't add role if part of radio group
            } else if (swatch.onclick || swatch.style.cursor === 'pointer') {
              swatch.setAttribute('role', 'button');
              if (!swatch.hasAttribute('tabindex')) {
                swatch.setAttribute('tabindex', '0');
              }
            }
          }
        }

        // Ensure associated images have alt text
        const img = swatch.querySelector('img:not([alt])');
        if (img) {
          img.setAttribute('alt', colorName ? colorName + ' swatch' : 'Color swatch');
        }
      });

      // Also handle standalone checkbox/radio inputs for colors
      const colorInputs = document.querySelectorAll('input[name*="color" i]:not([aria-label]), input[name*="Color"]:not([aria-label])');
      colorInputs.forEach((input) => {
        if (this.hasAccessibleLabel(input)) return;

        const label = input.value || input.dataset.value || '';
        if (label) {
          input.setAttribute('aria-label', this.humanizeName(label) + ' color option');
        }
      });
    },

    /**
     * Enhance Optis/BSS images specifically
     * These often load from S3 and lack alt text
     */
    enhanceOptisImages: function() {
      const optisImageSelectors = [
        '[class*="bss-po"] img:not([alt])',
        '[class*="bss_po"] img:not([alt])',
        '[class*="bss-po"] img[alt=""]',
        '[class*="bss_po"] img[alt=""]',
        'img[src*="bss-commerce"]',
        'img[src*="option-image"]',
        'img[src*="swatch"]',
        '.bss-po-swatch img',
        '.bss_po_swatch img'
      ];

      const images = document.querySelectorAll(optisImageSelectors.join(','));

      images.forEach((img) => {
        // Skip if already has meaningful alt
        if (img.alt && img.alt !== '' && img.alt !== 'undefined') return;

        let altText = '';

        // 1. Try parent swatch/option label
        const swatch = img.closest('[class*="swatch"], [class*="thumb"], [class*="option"]');
        if (swatch) {
          altText = swatch.getAttribute('aria-label') ||
                    swatch.title ||
                    swatch.dataset.value ||
                    '';
        }

        // 2. Try sibling text elements
        if (!altText) {
          const parent = img.parentElement;
          if (parent) {
            const textEl = parent.querySelector('.title, .label, span');
            if (textEl) altText = textEl.textContent.trim();
          }
        }

        // 3. Try title attribute on image
        if (!altText && img.title) {
          altText = img.title;
        }

        // 4. Extract from filename
        if (!altText && img.src) {
          const filename = img.src.split('/').pop().split('?')[0];
          // Remove file extension and common prefixes
          altText = filename
            .replace(/\.(jpg|jpeg|png|gif|webp|svg)$/i, '')
            .replace(/^(thumb|image|img|swatch)[-_]/i, '');
          altText = this.humanizeName(altText);
        }

        // 5. Check if it's a color swatch by analyzing styles
        if (!altText) {
          const style = window.getComputedStyle(img);
          const bgColor = style.backgroundColor;
          if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
            altText = 'Color option';
          }
        }

        // Apply alt text
        if (altText) {
          img.setAttribute('alt', altText);
        } else {
          // Mark as decorative if we truly can't determine purpose
          // but it's inside a labeled container
          const labeledParent = img.closest('[aria-label]');
          if (labeledParent) {
            img.setAttribute('alt', '');
            img.setAttribute('role', 'presentation');
          } else {
            // Generic fallback
            img.setAttribute('alt', 'Product option image');
          }
        }
      });
    },

    /**
     * Announce option changes to screen readers
     */
    setupOptisChangeAnnouncements: function() {
      // Listen for changes on Optis selects
      const selectors = [
        '[class*="bss-po"] select',
        '[class*="bss_po"] select',
        '[class*="bss-po"] input',
        '[class*="bss_po"] input'
      ];

      const inputs = document.querySelectorAll(selectors.join(','));

      inputs.forEach((input) => {
        if (input.dataset.a11yAnnounce) return;
        input.dataset.a11yAnnounce = 'true';

        input.addEventListener('change', (e) => {
          const target = e.target;
          let message = '';

          if (target.tagName === 'SELECT') {
            const selectedOption = target.options[target.selectedIndex];
            const label = target.getAttribute('aria-label') || 'Option';
            message = `${label}: ${selectedOption.textContent} selected`;
          } else if (target.type === 'checkbox') {
            const label = target.getAttribute('aria-label') || target.name || 'Option';
            message = target.checked ? `${label} checked` : `${label} unchecked`;
          } else if (target.type === 'radio') {
            const label = target.getAttribute('aria-label') || target.value || 'Option';
            message = `${label} selected`;
          }

          if (message) {
            this.announce(message);
          }
        });
      });

      // Listen for swatch clicks
      const swatches = document.querySelectorAll('[class*="bss-po-swatch"], [class*="bss_po_swatch"]');
      swatches.forEach((swatch) => {
        if (swatch.dataset.a11yAnnounce) return;
        swatch.dataset.a11yAnnounce = 'true';

        swatch.addEventListener('click', () => {
          const label = swatch.getAttribute('aria-label') || 'Option';
          this.announce(`${label} selected`);
        });
      });
    },

    // Helper functions

    /**
     * Check if element has an accessible label
     */
    hasAccessibleLabel: function(element) {
      const id = element.id;

      // Check for associated label
      if (id && document.querySelector(`label[for="${id}"]`)) return true;

      // Check for aria-label or aria-labelledby
      if (element.getAttribute('aria-label') || element.getAttribute('aria-labelledby')) return true;

      // Check for wrapper label
      if (element.closest('label')) return true;

      // Check for title
      if (element.getAttribute('title')) return true;

      return false;
    },

    /**
     * Find label text from various sources
     */
    findLabelText: function(element) {
      // Check placeholder
      if (element.placeholder) return element.placeholder;

      // Check name attribute
      if (element.name) return this.humanizeName(element.name);

      // Check nearby label-like elements
      const parent = element.parentElement;
      if (parent) {
        const labelLike = parent.querySelector('.label, .form-label, span:first-child');
        if (labelLike && labelLike.textContent.trim()) {
          return labelLike.textContent.trim();
        }
      }

      // Check previous sibling
      const prev = element.previousElementSibling;
      if (prev && (prev.tagName === 'SPAN' || prev.tagName === 'LABEL' || prev.tagName === 'DIV')) {
        const text = prev.textContent.trim();
        if (text && text.length < 50) return text;
      }

      return null;
    },

    /**
     * Convert programmatic name to human readable
     */
    humanizeName: function(name) {
      return name
        .replace(/[-_\[\]]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/^./, str => str.toUpperCase());
    },

    /**
     * Generate unique ID
     */
    generateId: function(prefix) {
      return prefix + '-' + Math.random().toString(36).substr(2, 9);
    }
  };

  // Initialize
  A11yEnhancements.init();

  // Expose for external use
  window.A11yEnhancements = A11yEnhancements;

  /**
   * QQ Video Gallery popup accessibility patch
   * Adds Escape key, focus management, and aria-hidden toggling
   */
  (function patchVideoGalleryPopup() {
    var popup = document.getElementById('qq-popup');
    if (!popup) return;

    var lastFocused = null;

    // Watch for popup open/close via class changes
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.attributeName !== 'class') return;
        var isOpen = popup.classList.contains('qq-open');
        popup.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        if (isOpen) {
          lastFocused = document.activeElement;
          var closeBtn = popup.querySelector('.qq-close');
          if (closeBtn) closeBtn.focus();
        } else if (lastFocused) {
          lastFocused.focus();
          lastFocused = null;
        }
      });
    });
    observer.observe(popup, { attributes: true });

    // Escape key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && popup.classList.contains('qq-open')) {
        var closeBtn = popup.querySelector('.qq-close');
        if (closeBtn) closeBtn.click();
      }
    });
  })();

})();
